import { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@roger/ui-components';
import { getBillingCycleInvoice } from '../../../services/api';
import type { InvoiceResponse } from '../../../services/api/types.gen';
import { getBusinessErrorMessage } from '../../../utils/apiErrorDetail';
import { formatMoneyDisplay } from '../../../utils/currencyUtils';
import { formatDateToMMDDYYYY } from '../../../utils/dateUtils';
import { getHttpStatus } from '../../../utils/httpErrorUtils';
import { BillingCycleInvoiceDownloadButton } from '../BillingCycleInvoiceDownloadButton';
import { SavedDocumentPanel } from './SavedDocumentPanel';

const LOAD_ERROR = 'Unable to load the invoice.';

const money = (value: string | number | null | undefined) =>
  formatMoneyDisplay(value) ?? '';

/**
 * An invoice-billed cycle's invoice, in place of the UB-04 preview.
 * Read-only, built from the last saved worksheet; editing stays in the
 * worksheet editor beside it. `canRefresh` is false for a claim whose status
 * refuses a Refresh, so the panel does not ask for one.
 */
export function InvoicePanel({
  billingCycleId,
  reloadKey,
  stale,
  disabled = false,
  canRefresh = true,
}: {
  billingCycleId: string;
  reloadKey: number;
  stale: boolean;
  disabled?: boolean;
  canRefresh?: boolean;
}) {
  const [invoice, setInvoice] = useState<InvoiceResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A 4xx answer names what to fix, and loading again cannot fix it.
  const [retryable, setRetryable] = useState(true);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (disabled) return;
      setIsLoading(true);
      setError(null);
      setRetryable(true);
      try {
        const resp = await getBillingCycleInvoice({
          path: { billing_cycle_id: billingCycleId },
        });
        if (resp.error || !resp.data) {
          const status = getHttpStatus(resp);
          const refused = status !== undefined && status >= 400 && status < 500;
          console.error('Failed to load invoice:', resp.error);
          if (!cancelled) {
            setInvoice(null);
            setRetryable(!refused);
            setError(getBusinessErrorMessage(resp) ?? LOAD_ERROR);
          }
          return;
        }
        if (!cancelled) setInvoice(resp.data);
      } catch (err) {
        console.error('Failed to load invoice:', err);
        if (!cancelled) {
          setInvoice(null);
          setError(LOAD_ERROR);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [billingCycleId, disabled, reloadKey, retryKey]);

  const period = [invoice?.statement_from, invoice?.statement_through]
    .filter((value): value is string => !!value)
    .map(value => formatDateToMMDDYYYY(value))
    .join(' – ');

  return (
    <SavedDocumentPanel
      title='Invoice'
      subtitle='Shows the last saved worksheet'
      ariaLabel='Invoice'
      stale={stale}
      notice={
        disabled ? (
          <p className='text-sm text-muted-foreground'>
            {canRefresh
              ? 'Refresh the worksheet to see the invoice.'
              : "This claim's worksheet can't be refreshed in its current status, so its invoice can't be shown."}
          </p>
        ) : null
      }
      isLoading={isLoading && !invoice}
      loadingLabel='Loading invoice'
      error={error}
      onRetry={retryable ? () => setRetryKey(key => key + 1) : undefined}
    >
      {invoice && !disabled ? (
        <div className='space-y-3 text-sm'>
          <dl className='grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2'>
            <div>
              <dt className='text-xs text-muted-foreground'>Bill to</dt>
              <dd>{invoice.bill_to_name ?? '-'}</dd>
            </div>
            <div>
              <dt className='text-xs text-muted-foreground'>Invoice number</dt>
              <dd className='font-mono'>{invoice.invoice_number}</dd>
            </div>
            <div>
              <dt className='text-xs text-muted-foreground'>Member ID</dt>
              <dd>{invoice.member_id ?? '-'}</dd>
            </div>
            <div>
              <dt className='text-xs text-muted-foreground'>Service period</dt>
              <dd>{period || '-'}</dd>
            </div>
          </dl>

          <div className='sm:hidden' data-testid='invoice-cards'>
            <ul className='divide-y divide-border border-y border-border'>
              {invoice.lines.map(line => (
                <li key={line.row_id} className='space-y-0.5 py-2'>
                  <div className='break-words font-medium'>
                    {line.description}
                  </div>
                  <div className='flex justify-between gap-3 text-xs text-muted-foreground'>
                    <span>
                      {line.service_date
                        ? formatDateToMMDDYYYY(line.service_date)
                        : ''}
                    </span>
                    <span className='font-mono'>
                      {line.revenue_code}
                      {line.hcpcs ? ` ${line.hcpcs}` : ''}
                    </span>
                  </div>
                  <div className='flex justify-between gap-3'>
                    <span className='text-xs text-muted-foreground'>
                      {Number(line.units)}
                      {line.non_covered
                        ? ''
                        : ` × ${money(line.allowed_amount)}`}
                    </span>
                    <span className='font-medium'>
                      {line.non_covered
                        ? 'Non-covered'
                        : money(line.line_total)}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
            <div className='flex justify-between gap-3 pt-2 font-semibold'>
              <span>Total</span>
              <span>{money(invoice.total)}</span>
            </div>
          </div>

          <Table
            className='hidden w-full table-fixed text-xs sm:table'
            data-testid='invoice-lines'
          >
            <colgroup>
              <col className='w-[4.5rem]' />
              <col />
              <col className='w-[4.25rem]' />
              <col className='w-9' />
              <col className='w-[4rem]' />
              <col className='w-[4.5rem]' />
            </colgroup>
            <TableHeader>
              <TableRow>
                <TableHead className='px-1'>Date</TableHead>
                <TableHead className='px-1'>Service</TableHead>
                <TableHead className='px-1'>Rev / HCPCS</TableHead>
                <TableHead className='px-1 text-right'>Units</TableHead>
                <TableHead className='px-1 text-right'>Rate</TableHead>
                <TableHead className='px-1 text-right'>Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoice.lines.map(line => (
                <TableRow key={line.row_id}>
                  <TableCell className='px-1 align-top'>
                    {line.service_date
                      ? formatDateToMMDDYYYY(line.service_date)
                      : ''}
                  </TableCell>
                  <TableCell
                    className='px-1 align-top break-words whitespace-normal'
                    title={line.description ?? undefined}
                  >
                    {line.description}
                  </TableCell>
                  <TableCell className='px-1 align-top font-mono break-words whitespace-normal'>
                    {line.revenue_code}
                    {line.hcpcs ? ` ${line.hcpcs}` : ''}
                  </TableCell>
                  <TableCell className='px-1 text-right align-top'>
                    {Number(line.units)}
                  </TableCell>
                  <TableCell className='px-1 text-right align-top'>
                    {line.non_covered ? '' : money(line.allowed_amount)}
                  </TableCell>
                  <TableCell className='px-1 text-right align-top'>
                    {line.non_covered ? 'Non-covered' : money(line.line_total)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell
                  colSpan={5}
                  className='px-1 text-right font-semibold'
                >
                  Total
                </TableCell>
                <TableCell className='px-1 text-right font-semibold'>
                  {money(invoice.total)}
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>

          <div className='w-fit'>
            <BillingCycleInvoiceDownloadButton
              billingCycleId={billingCycleId}
            />
          </div>
        </div>
      ) : null}
    </SavedDocumentPanel>
  );
}
